/** Investor Buy-Box Matcher page (v1 investorMatchesView). Pick an investor and
 * every lead is scored against their buy-box (Flip + BRRRR) using v2's ROI
 * engine. Full matches vs partial ("needs data") are split, each card showing
 * the numbers + the per-criterion pass/fail/unknown chips. Editing writes the
 * whole INVESTORS array through the settings door (Phase 22), gated behind
 * liveFire `settingsEdit`. */
import { useMemo, useState } from "react";
import { mergeMaster } from "../roi/math";
import { useConfirmDelete } from "../common/ConfirmDelete";
import {
  allInvestors, matchInvestor, criteriaSummary, dtLabel, emptyCriteria, invId, usd,
  type Investor, type MatchResult, type Criteria, type DealType, type Logic, type TriState, type Structure,
} from "./api";
import type { Lead } from "../../types";

type Scored = { lead: Lead; r: MatchResult };

export function InvestorsPage({
  leads, orgSettings, onSave, canEdit, onBack,
}: {
  leads: Lead[];
  orgSettings: Record<string, unknown>;
  onSave: (all: Investor[]) => void;
  canEdit: boolean;
  onBack?: () => void;
}) {
  const investors = useMemo(() => allInvestors(orgSettings), [orgSettings]);
  const nrMaster = useMemo(() => mergeMaster(orgSettings["NR_MASTER"]), [orgSettings]);
  const nrBy = orgSettings["NR_BY"];

  const [selId, setSelId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Investor | null>(null);

  const selected = investors.find((x) => x.id === selId) ?? investors[0] ?? null;

  // count matches per investor (for the rail badges) + the selected results
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const inv of investors) {
      let n = 0;
      for (const lead of leads) {
        const r = matchInvestor(inv, lead, nrMaster, nrBy);
        if (r.status === "full" || r.status === "partial") n++;
      }
      m.set(inv.id, n);
    }
    return m;
  }, [investors, leads, nrMaster, nrBy]);

  const results = useMemo(() => {
    if (!selected) return { full: [] as Scored[], partial: [] as Scored[] };
    const full: Scored[] = [], partial: Scored[] = [];
    for (const lead of leads) {
      const r = matchInvestor(selected, lead, nrMaster, nrBy);
      if (r.status === "full") full.push({ lead, r });
      else if (r.status === "partial") partial.push({ lead, r });
    }
    return { full, partial };
  }, [selected, leads, nrMaster, nrBy]);

  const startNew = () => setEditing({ id: "", name: "", dealType: "either", logic: "all", criteria: emptyCriteria() });
  const startEdit = (inv: Investor) => setEditing({ ...inv, criteria: { ...inv.criteria } });

  const commit = (inv: Investor) => {
    const withId: Investor = inv.id ? inv : { ...inv, id: invId() };
    const next = inv.id ? investors.map((x) => (x.id === inv.id ? withId : x)) : [...investors, withId];
    onSave(next);
    setSelId(withId.id);
    setEditing(null);
  };
  // v1 'Delete investor "NAME"?' — typed-delete gated
  const confirmDel = useConfirmDelete();
  const remove = (id: string) => {
    const inv = investors.find((x) => x.id === id);
    confirmDel.ask(`Delete investor "${inv?.name || ""}"?`, () => {
      onSave(investors.filter((x) => x.id !== id));
      if (selId === id) setSelId(null);
    });
  };

  return (
    <div className="leads exppage">
      <div className="exphead">
        <div>
          {onBack ? <button className="btn slim invback" onClick={onBack}>← Properties</button> : null}
          <h1>Investor Buy-Box Matcher</h1>
        </div>
        <div className="expacts">
          {canEdit ? <button className="btn slim primary" onClick={startNew}>+ New investor</button> : null}
        </div>
      </div>
      <div className="expqbo">
        Save an investor's buy-box and every property is scored against it — Flip and BRRRR — using your ROI defaults.
        {canEdit ? "" : " Editing is view-only on this device."}
      </div>

      {editing ? (
        <InvestorEditor
          initial={editing}
          onCancel={() => setEditing(null)}
          onSave={commit}
        />
      ) : null}

      {investors.length === 0 && !editing ? (
        <div className="expempty">No investors yet{canEdit ? " — click New investor to add a buy-box." : "."}</div>
      ) : (
        <>
          {investors.length ? (
            <div className="invchips">
              {investors.map((inv) => (
                <button
                  key={inv.id}
                  className={"invchip" + (selected && inv.id === selected.id ? " active" : "")}
                  onClick={() => setSelId(inv.id)}
                >
                  <span className="invchipname">{inv.name || "Investor"}</span>
                  <span className={"invbadge " + inv.dealType}>{dtLabel(inv.dealType)}</span>
                  <span className="invchipct">{counts.get(inv.id) ?? 0}</span>
                </button>
              ))}
            </div>
          ) : null}

          {selected ? (
            <>
              <div className="invhead">
                <div className="invheadmain">
                  <div className="invheadtop">
                    <strong>{selected.name || "Investor"}</strong>
                    <span className={"invbadge " + selected.dealType}>{dtLabel(selected.dealType)}</span>
                    <span className="expcat">Match {selected.logic === "any" ? "ANY" : "ALL"}</span>
                  </div>
                  <div className="invcrit">{criteriaSummary(selected)}</div>
                </div>
                {canEdit ? (
                  <div className="expacts">
                    <button className="btn slim" onClick={() => startEdit(selected)}>Edit</button>
                    <button className="rowbtn del" title="Delete investor" onClick={() => remove(selected.id)}>✕</button>
                  </div>
                ) : null}
              </div>

              <div className="dkpis exptiles">
                <div className="dkpi"><span className="lbl">Full matches</span><strong className="good">{results.full.length}</strong></div>
                <div className="dkpi"><span className="lbl">Partial (needs data)</span><strong className="owed">{results.partial.length}</strong></div>
                <div className="dkpi"><span className="lbl">Scanned</span><strong>{leads.length}</strong></div>
              </div>

              {results.full.length === 0 && results.partial.length === 0 ? (
                <div className="expempty">No properties match this buy-box yet.</div>
              ) : (
                <>
                  {results.full.length ? <div className="invsec">Full matches <span className="expcat">{results.full.length}</span></div> : null}
                  {results.full.map(({ lead, r }) => <MatchCard key={lead.id} inv={selected} lead={lead} r={r} />)}
                  {results.partial.length ? <div className="invsec">Partial — fill the flagged fields <span className="expcat">{results.partial.length}</span></div> : null}
                  {results.partial.map(({ lead, r }) => <MatchCard key={lead.id} inv={selected} lead={lead} r={r} />)}
                </>
              )}
            </>
          ) : null}
        </>
      )}
      {confirmDel.modal}
    </div>
  );
}

function MatchCard({ inv, lead, r }: { inv: Investor; lead: Lead; r: MatchResult }) {
  const dt = inv.dealType;
  const nums: [string, string][] = [];
  if (dt === "flip" || dt === "either") {
    nums.push(["Profit", r.hasFin ? usd(r.flip.profit) : "—"]);
    nums.push(["ROI", r.hasFin ? r.flip.roi.toFixed(1) + "%" : "—"]);
  }
  if (dt === "brrr" || dt === "either") {
    nums.push(["CoC", r.hasFin ? (r.brrr.coc === Infinity ? "∞" : r.brrr.coc.toFixed(1) + "%") : "—"]);
    nums.push(["Cap", r.hasFin ? r.brrr.capRate.toFixed(1) + "%" : "—"]);
    nums.push(["Cash flow", r.hasFin ? usd(r.brrr.cfMo) + "/mo" : "—"]);
    nums.push(["Cash left", r.hasFin ? r.brrr.cashLeftPct.toFixed(0) + "%" : "—"]);
    nums.push(["Forced eq.", r.hasFin ? usd(r.brrr.forcedEquity) : "—"]);
  }
  const chips = [
    ...r.attr,
    ...r.flipChecks.map((c) => ({ label: `${c[0]} (${c[2]})`, state: c[1] === null ? "unknown" : c[1] ? "pass" : "fail" } as const)),
    ...r.brrrChecks.map((c) => ({ label: `${c[0]} (${c[2]})`, state: c[1] === null ? "unknown" : c[1] ? "pass" : "fail" } as const)),
  ];
  return (
    <div className={"invcard" + (r.status === "partial" ? " partial" : "")}>
      <div className="invcardhead">
        <div className="invcardaddr">
          <strong>{lead.address || "Property"}</strong>
          <span className="dim">{lead.county ?? ""}</span>
        </div>
        <span className={"invmatch " + r.status}>{r.status === "partial" ? "Needs data" : "Full match"}</span>
      </div>
      <div className="invnums">
        {nums.map(([k, v]) => <span key={k}>{k}: <b>{v}</b></span>)}
      </div>
      <div className="invchecks">
        {chips.map((x, i) => <span key={i} className={"invchk " + x.state}><span className="invchkico">{x.state === "pass" ? "✓" : x.state === "fail" ? "✗" : "?"}</span>{x.label}</span>)}
      </div>
    </div>
  );
}

// ---- editor -----------------------------------------------------------------

const numOrNull = (s: string): number | null => {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

function InvestorEditor({ initial, onCancel, onSave }: { initial: Investor; onCancel: () => void; onSave: (inv: Investor) => void }) {
  const [name, setName] = useState(initial.name);
  const [dealType, setDealType] = useState<DealType>(initial.dealType);
  const [logic, setLogic] = useState<Logic>(initial.logic);
  const [c, setC] = useState<Criteria>(initial.criteria);
  const set = (patch: Partial<Criteria>) => setC((prev) => ({ ...prev, ...patch }));
  const ok = name.trim().length > 0;

  const submit = () => {
    if (!ok) return;
    onSave({ id: initial.id, name: name.trim(), dealType, logic, criteria: c });
  };

  const N = (label: string, key: keyof Criteria, suffix?: string) => (
    <label>
      <span>{label}{suffix ? ` (${suffix})` : ""}</span>
      <input type="number" value={(c[key] as number | null) ?? ""} onChange={(e) => set({ [key]: numOrNull(e.target.value) } as Partial<Criteria>)} />
    </label>
  );

  return (
    <div className="expform">
      <div className="expformtitle">{initial.id ? "Edit investor" : "New investor"}</div>
      <div className="expformgrid">
        <label className="wide"><span>Investor name</span><input value={name} placeholder="e.g. John Smith" onChange={(e) => setName(e.target.value)} /></label>
        <label><span>Looking for</span>
          <select value={dealType} onChange={(e) => setDealType(e.target.value as DealType)}>
            <option value="either">Flip or BRRRR</option><option value="flip">Flip only</option><option value="brrr">BRRRR only</option>
          </select>
        </label>
        <label><span>Financials must meet</span>
          <select value={logic} onChange={(e) => setLogic(e.target.value as Logic)}>
            <option value="all">ALL thresholds</option><option value="any">ANY threshold</option>
          </select>
        </label>
      </div>

      <div className="scgrouptitle">Property criteria (blank = ignore)</div>
      <div className="expformgrid">
        {N("Year built min", "yearMin")}
        {N("Year built max", "yearMax")}
        {N("Price min", "priceMin", "$")}
        {N("Price max", "priceMax", "$")}
        <label><span>HOA</span>
          <select value={c.hoa} onChange={(e) => set({ hoa: e.target.value as TriState })}><option value="any">Any</option><option value="no">No HOA</option><option value="yes">HOA OK</option></select>
        </label>
        <label><span>55+ community</span>
          <select value={c.s55} onChange={(e) => set({ s55: e.target.value as TriState })}><option value="any">Any</option><option value="no">Not 55+</option><option value="yes">55+ OK</option></select>
        </label>
        <label><span>Pool</span>
          <select value={c.pool} onChange={(e) => set({ pool: e.target.value as TriState })}><option value="any">Any</option><option value="yes">Has pool</option><option value="no">No pool</option></select>
        </label>
        <label><span>Structure</span>
          <select value={c.structure} onChange={(e) => set({ structure: e.target.value as Structure })}><option value="any">Any</option><option value="block">Block/CBS</option><option value="wood">Wood frame</option><option value="other">Other</option></select>
        </label>
      </div>

      <div className="scgrouptitle">Flip targets</div>
      <div className="expformgrid">
        {N("Min net profit", "minProfit", "$")}
        {N("Min ROI", "minRoi", "%")}
      </div>

      <div className="scgrouptitle">BRRRR targets</div>
      <div className="expformgrid">
        {N("Cash-on-cash min", "cocMin", "%")}
        {N("Cash-on-cash max", "cocMax", "%")}
        {N("Min cash flow / mo", "cfMin", "$")}
        {N("Min cap rate", "capMin", "%")}
        {N("Max cash left", "cashLeftMaxPct", "%")}
        {N("Min forced equity", "feMin", "$")}
        {N("Min forced equity", "fePctMin", "%")}
      </div>

      <div className="expformacts">
        <button className="btn slim primary" disabled={!ok} onClick={submit}>{initial.id ? "Save changes" : "Create investor"}</button>
        <button className="btn slim" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
